/**
 * API Service for NGUI
 *
 * This module provides a typed API client for communicating with the NGUI backend.
 * All functions include proper TypeScript types for requests and responses.
 */

import axios, { AxiosInstance, AxiosError, AxiosResponse } from 'axios';
import { reportNetworkError } from './errorReporting';
import type {
  Item,
  CreateItemRequest,
  UpdateItemRequest,
  HealthResponse,
  StatsResponse,
  GetItemsParams,
  APIError
} from '@/types/api';
import type { UserPreferences, NoiseReductionType } from '@/types/user';

// ============================================================
// API Client Configuration
// ============================================================

/**
 * Base API client instance with default configuration
 */
const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================
// Request Interceptor
// ============================================================

apiClient.interceptors.request.use(
  (config) => {
    // Add auth token from localStorage if available
    const token = localStorage.getItem('ngui_auth_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add timestamp to all requests for debugging
    console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`, config.data || '');
    return config;
  },
  (error: AxiosError) => {
    console.error('[API] Request error:', error);
    return Promise.reject(error);
  }
);

// ============================================================
// Response Interceptor
// ============================================================

// URLs where 404 is expected and should not be logged as errors
const EXPECTED_404_PATTERNS = [
  /\/processing\/[^/]+\/preview/,  // File previews - may not exist yet
  /\/files\/[^/]+\/preview/,       // File previews (alternate path)
];

const isExpected404 = (url: string | undefined, status: number | undefined): boolean => {
  if (status !== 404 || !url) return false;
  return EXPECTED_404_PATTERNS.some(pattern => pattern.test(url));
};

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log(`[API] Response ${response.status}:`, response.data);
    return response;
  },
  async (error: AxiosError<APIError>) => {
    const url = error.config?.url;
    const status = error.response?.status;

    // Skip error reporting for expected 404s (e.g., previews that don't exist yet)
    if (!isExpected404(url, status)) {
      // Report error to audit system
      await reportNetworkError(error, {
        url: url,
        method: error.config?.method,
        requestData: error.config?.data,
        responseData: error.response?.data,
        statusCode: status
      });
    }

    // Handle different error scenarios
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const message = error.response.data?.detail || error.message;

      console.error(`[API] Error ${status}:`, message);

      // Customize error messages based on status code
      switch (status) {
        case 401:
          // Unauthorized - token expired or invalid
          console.warn('[API] Authentication expired, logging out...');

          // Clear auth data from localStorage
          localStorage.removeItem('ngui_auth_token');
          localStorage.removeItem('ngui_auth_user');

          // Redirect to login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login?session_expired=true';
          }

          throw new Error('Session expired. Please log in again.');

        case 400:
          throw new Error(`Bad Request: ${message}`);
        case 403:
          throw new Error(`Forbidden: ${message}`);
        case 404:
          throw new Error(`Not Found: ${message}`);
        case 422:
          throw new Error(`Validation Error: ${message}`);
        case 500:
          throw new Error('Server Error: Please try again later');
        case 503:
          throw new Error('Service Unavailable: Server is temporarily down');
        default:
          throw new Error(message);
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('[API] No response:', error.request);
      throw new Error('Network Error: Unable to reach server');
    } else {
      // Error in request configuration
      console.error('[API] Error:', error.message);
      throw new Error(`Request Error: ${error.message}`);
    }
  }
);

// ============================================================
// Health & Status Endpoints
// ============================================================

/**
 * Public health status for banner display (no auth required)
 */
export interface PublicHealthStatus {
  status: 'healthy' | 'degraded' | 'unhealthy';
  message: string | null;
  affected_features: string[];
  last_check_at: string | null;
}

/**
 * Get public health status for system banner (NO AUTH REQUIRED)
 * This endpoint is used by the HealthBanner component to show system issues to all users.
 * Uses a separate axios instance without auth headers.
 */
export const getPublicHealthStatus = async (): Promise<PublicHealthStatus> => {
  // Use bare axios to avoid auth headers - this is a public endpoint
  const response = await axios.get<PublicHealthStatus>('/api/health/status', {
    timeout: 10000,  // 10 second timeout
  });
  return response.data;
};

/**
 * Check API health status
 * @returns Health check response with database status
 */
export const getHealth = async (): Promise<HealthResponse> => {
  const response = await apiClient.get<HealthResponse>('/health');
  return response.data;
};

/**
 * Get database statistics
 * @returns Statistics including item counts, categories, and activity
 */
export const getStats = async (): Promise<StatsResponse> => {
  const response = await apiClient.get<StatsResponse>('/stats');
  return response.data;
};

// ============================================================
// Item CRUD Endpoints
// ============================================================

/**
 * Get a list of items with optional filtering
 * @param params Query parameters for filtering and pagination
 * @returns Array of items matching the criteria
 */
export const getItems = async (params?: GetItemsParams): Promise<Item[]> => {
  const response = await apiClient.get<Item[]>('/items', { params });
  return response.data;
};

/**
 * Get a single item by ID
 * @param id Item ID
 * @returns The requested item
 * @throws Error if item not found (404)
 */
export const getItem = async (id: number): Promise<Item> => {
  const response = await apiClient.get<Item>(`/items/${id}`);
  return response.data;
};

/**
 * Create a new item
 * @param data Item data to create
 * @returns The created item with generated ID and timestamps
 */
export const createItem = async (data: CreateItemRequest): Promise<Item> => {
  const response = await apiClient.post<Item>('/items', data);
  return response.data;
};

/**
 * Update an existing item
 * @param id Item ID to update
 * @param data Fields to update (all optional)
 * @returns The updated item
 * @throws Error if item not found (404)
 */
export const updateItem = async (id: number, data: UpdateItemRequest): Promise<Item> => {
  const response = await apiClient.put<Item>(`/items/${id}`, data);
  return response.data;
};

/**
 * Delete an item
 * @param id Item ID to delete
 * @throws Error if item not found (404)
 */
export const deleteItem = async (id: number): Promise<void> => {
  await apiClient.delete(`/items/${id}`);
};

// ============================================================
// User Preferences Endpoints
// ============================================================

/**
 * Get current user's preferences
 * @returns User preferences including noise_reduction and avatar_enabled
 */
export const getUserPreferences = async (): Promise<UserPreferences> => {
  const response = await apiClient.get<UserPreferences>('/users/me/preferences');
  return response.data;
};

/**
 * Update current user's preferences (noise_reduction only)
 * @param noise_reduction The noise reduction setting
 * @returns Updated user preferences
 */
export const updateUserPreferences = async (
  noise_reduction: NoiseReductionType
): Promise<UserPreferences> => {
  const response = await apiClient.put<UserPreferences>('/users/me/preferences', {
    noise_reduction
  });
  return response.data;
};

// ============================================================
// Language Preferences
// ============================================================

export interface LanguageInfo {
  code: string;
  name: string;
  native: string;
  flag: string;
}

export interface AvailableLanguagesResponse {
  languages: LanguageInfo[];
}

export interface LanguagePreferencesResponse {
  language_preferences: string[];
}

/**
 * Get list of available languages for voice interface
 */
export const getAvailableLanguages = async (): Promise<LanguageInfo[]> => {
  const response = await apiClient.get<AvailableLanguagesResponse>('/settings/languages');
  return response.data.languages;
};

/**
 * Get current user's language preferences
 */
export const getLanguagePreferences = async (): Promise<string[]> => {
  const response = await apiClient.get<LanguagePreferencesResponse>('/settings/language-preferences');
  return response.data.language_preferences;
};

/**
 * Update current user's language preferences
 * @param languages Array of language codes
 */
export const updateLanguagePreferences = async (languages: string[]): Promise<string[]> => {
  const response = await apiClient.put<{ success: boolean; language_preferences: string[] }>(
    '/settings/language-preferences',
    { languages }
  );
  return response.data.language_preferences;
};

/**
 * Get current user's preferred language for voice AI
 */
export const getPreferredLanguage = async (): Promise<string> => {
  const response = await apiClient.get<{ preferred_language: string }>('/settings/preferred-language');
  return response.data.preferred_language;
};

/**
 * Update current user's preferred language for voice AI
 * @param language Language code (e.g., 'en', 'it', 'mt')
 */
export const updatePreferredLanguage = async (language: string): Promise<string> => {
  const response = await apiClient.put<{ success: boolean; preferred_language: string }>(
    '/settings/preferred-language',
    { language }
  );
  return response.data.preferred_language;
};

// ============================================================
// Settings/Profile Endpoints
// ============================================================

export interface UserProfile {
  id: number;
  username: string;
  full_name: string;
  email: string;
  role: string;
  avatar_url?: string | null;
  created_at: string;
  last_login: string | null;
}

export interface ProfileUpdateRequest {
  full_name?: string;
  email?: string;
}

export interface PasswordChangeRequest {
  current_password: string;
  new_password: string;
}

/**
 * Get current user's profile
 */
export const getProfile = async (): Promise<{ profile: UserProfile }> => {
  const response = await apiClient.get('/settings/profile');
  return response.data;
};

/**
 * Update current user's profile
 */
export const updateProfile = async (data: ProfileUpdateRequest): Promise<{ success: boolean; profile: UserProfile }> => {
  const response = await apiClient.put('/settings/profile', data);
  return response.data;
};

/**
 * Change current user's password
 */
export const changePassword = async (data: PasswordChangeRequest): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.put('/settings/password', data);
  return response.data;
};

// ============================================================
// Billing Endpoints
// ============================================================

export interface BillingPlan {
  id: string;
  name: string;
  description: string;
  price_monthly: number;
  price_yearly: number;
  currency: string;
  features: string[];
  limits: {
    voice_minutes: number;
    api_calls: number;
    storage_mb: number;
  };
  overage_rates: {
    voice_minutes: number;
    api_calls: number;
    storage_mb: number;
  };
}

export interface Subscription {
  plan_id: string;
  plan_name: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
}

export interface UsageStats {
  voice_minutes_used: number;
  voice_minutes_limit: number;
  api_calls_used: number;
  api_calls_limit: number;
  storage_used_mb: number;
  storage_limit_mb: number;
}

export type DocumentType = 'INVOICE' | 'RECEIPT' | 'CREDIT_NOTE';

export interface Invoice {
  id: string;
  document_type: DocumentType;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  paid_at: string | null;
  description: string;
  pdf_path: string | null;
}

// ============================================================
// Balance & Transaction Types
// ============================================================

export interface Balance {
  amount: number;  // in cents
  amount_formatted: string;  // e.g., "€50.00"
  currency: string;
  updated_at: string;
}

export type TransactionType = 'TOP_UP' | 'SUBSCRIPTION' | 'OVERAGE' | 'REFUND' | 'ADJUSTMENT';

export interface BalanceTransaction {
  id: string;
  user_id: number;
  type: TransactionType;
  amount: number;  // in cents, positive=credit, negative=debit
  balance_before: number;
  balance_after: number;
  description: string;
  reference_id: string | null;
  payment_method_id: string | null;  // ID of payment method used
  metadata: string | null;  // JSON string
  created_at: string;
}

export interface OverageBreakdown {
  voice_minutes: { over: number; rate: number; charge: number };
  api_calls: { over: number; rate: number; charge: number };
  storage_mb: { over: number; rate: number; charge: number };
}

export interface OverageInfo {
  breakdown: OverageBreakdown;
  total_cents: number;
  total_formatted: string;
}

/**
 * Get available billing plans
 */
export const getBillingPlans = async (): Promise<{ plans: BillingPlan[] }> => {
  const response = await apiClient.get('/billing/plans');
  return response.data;
};

/**
 * Get current user's subscription
 */
export const getSubscription = async (): Promise<{ subscription: Subscription; plan: BillingPlan }> => {
  const response = await apiClient.get('/billing/subscription');
  return response.data;
};

/**
 * Get current user's usage statistics
 */
export const getUsage = async (): Promise<{ usage: UsageStats; period_start: string }> => {
  const response = await apiClient.get('/billing/usage');
  return response.data;
};

/**
 * Get current user's invoices
 */
export const getInvoices = async (): Promise<{ invoices: Invoice[] }> => {
  const response = await apiClient.get('/billing/invoices');
  return response.data;
};

/**
 * Subscribe to a billing plan using balance
 * @param planId - The plan to subscribe to
 * @param useBalance - Whether to charge from balance (default true)
 */
export const subscribeToPlan = async (
  planId: string,
  useBalance: boolean = true
): Promise<{
  success: boolean;
  subscription: Subscription;
  plan: BillingPlan;
  transaction?: BalanceTransaction;
  invoice_id?: string;
  charged?: { amount: number; amount_formatted: string };
  balance?: { amount: number; amount_formatted: string };
}> => {
  const response = await apiClient.post('/billing/subscribe', {
    plan_id: planId,
    use_balance: useBalance
  });
  return response.data;
};

// ============================================================
// Balance API Functions
// ============================================================

/**
 * Get current user's balance and recent transactions
 */
export const getBalance = async (): Promise<{
  balance: Balance;
  recent_transactions: BalanceTransaction[];
}> => {
  const response = await apiClient.get('/billing/balance');
  return response.data;
};

/**
 * Top up balance (add funds)
 * @param amount - Amount in EUR (e.g., 50.00)
 * @param paymentMethod - Payment method display (card, bank_transfer) - legacy
 * @param paymentMethodId - ID of saved payment method (PM-xxx)
 */
export const topUpBalance = async (
  amount: number,
  paymentMethod: string = 'card',
  paymentMethodId?: string
): Promise<{
  success: boolean;
  message: string;
  transaction: BalanceTransaction;
  receipt_id: string;
  new_balance: { amount: number; amount_formatted: string };
}> => {
  const response = await apiClient.post('/billing/top-up', {
    amount,
    payment_method: paymentMethod,
    payment_method_id: paymentMethodId
  });
  return response.data;
};

/**
 * Get transaction history with pagination
 */
export const getTransactions = async (
  limit: number = 20,
  offset: number = 0,
  type?: TransactionType
): Promise<{
  transactions: BalanceTransaction[];
  pagination: { total: number; limit: number; offset: number; has_more: boolean };
}> => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (type) params.append('type', type);
  const response = await apiClient.get(`/billing/transactions?${params}`);
  return response.data;
};

/**
 * Get single transaction details
 */
export const getTransaction = async (transactionId: string): Promise<{ transaction: BalanceTransaction }> => {
  const response = await apiClient.get(`/billing/transactions/${transactionId}`);
  return response.data;
};

/**
 * Get current overage preview (without charging)
 */
export const getOveragePreview = async (): Promise<{
  plan: string;
  overage: OverageInfo;
}> => {
  const response = await apiClient.get('/billing/overage');
  return response.data;
};

/**
 * Charge current overage to balance
 */
export const chargeOverage = async (): Promise<{
  success: boolean;
  message: string;
  charged?: number;
  transaction?: BalanceTransaction;
  overage_breakdown?: OverageBreakdown;
  new_balance?: { amount: number; amount_formatted: string };
}> => {
  const response = await apiClient.post('/billing/charge-overage');
  return response.data;
};

/**
 * Create a checkout session for paid plans
 */
export const createCheckout = async (planId: string): Promise<{ checkout_url: string; session_id: string; amount: number }> => {
  const response = await apiClient.post(`/billing/create-checkout?plan_id=${planId}`);
  return response.data;
};

/**
 * Confirm payment and activate subscription
 */
export const confirmPayment = async (planId: string, paymentIntentId: string): Promise<{ success: boolean; subscription: Subscription; plan: BillingPlan; message: string }> => {
  const response = await apiClient.post(`/billing/confirm-payment?plan_id=${planId}&payment_intent_id=${paymentIntentId}`);
  return response.data;
};

// ============================================================
// Billing Info Endpoints
// ============================================================

export interface BillingInfo {
  id?: number;
  user_id?: number;
  company_name: string;
  address_line1: string;
  address_line2: string;
  city: string;
  postal_code: string;
  country: string;
  vat_number: string;
  tax_id: string;
  created_at?: string;
  updated_at?: string;
}

export interface BillingInfoUpdate {
  company_name?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  vat_number?: string;
  tax_id?: string;
}

/**
 * Get current user's billing information
 */
export const getBillingInfo = async (): Promise<{ billing_info: BillingInfo }> => {
  const response = await apiClient.get('/billing/info');
  return response.data;
};

/**
 * Update current user's billing information
 */
export const updateBillingInfo = async (data: BillingInfoUpdate): Promise<{ success: boolean; billing_info: BillingInfo }> => {
  const response = await apiClient.put('/billing/info', data);
  return response.data;
};

/**
 * Get invoice PDF download URL
 */
export const getInvoicePdfUrl = (invoiceId: string): string => {
  return `/api/billing/invoices/${invoiceId}/pdf`;
};

// ============================================================
// Payment Method Types & Endpoints
// ============================================================

export type CardBrand = 'Visa' | 'Mastercard' | 'Amex' | 'Discover' | 'Card';

export interface PaymentMethod {
  id: string;
  type: 'card';
  brand: CardBrand;
  last4: string;
  exp_month: number;
  exp_year: number;
  cardholder_name: string | null;
  is_default: boolean;
  created_at: string;
}

export interface AddPaymentMethodRequest {
  card_number: string;
  exp_month: number;
  exp_year: number;
  cardholder_name?: string;
  set_as_default?: boolean;
}

/**
 * Get all payment methods for current user
 */
export const getPaymentMethods = async (): Promise<{ payment_methods: PaymentMethod[] }> => {
  const response = await apiClient.get('/billing/payment-methods');
  return response.data;
};

/**
 * Add a new payment method (card)
 */
export const addPaymentMethod = async (data: AddPaymentMethodRequest): Promise<{ success: boolean; payment_method: PaymentMethod }> => {
  const response = await apiClient.post('/billing/payment-methods', data);
  return response.data;
};

/**
 * Set a payment method as default
 */
export const setDefaultPaymentMethod = async (paymentMethodId: string): Promise<{ success: boolean; payment_method: PaymentMethod }> => {
  const response = await apiClient.post('/billing/payment-methods/set-default', {
    payment_method_id: paymentMethodId
  });
  return response.data;
};

/**
 * Delete a payment method
 */
export const deletePaymentMethod = async (paymentMethodId: string): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.delete(`/billing/payment-methods/${paymentMethodId}`);
  return response.data;
};

/**
 * Download invoice PDF (opens in new tab or triggers download)
 */
export const downloadInvoicePdf = async (invoiceId: string): Promise<void> => {
  const response = await apiClient.get(`/billing/invoices/${invoiceId}/pdf`, {
    responseType: 'blob'
  });

  // Create blob URL and trigger download
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${invoiceId}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// ============================================================
// Support Ticket Endpoints
// ============================================================

export interface SupportTicket {
  id: number;
  user_id: number;
  username: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  description: string;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
}

export interface TicketMessage {
  id: number;
  ticket_id: number;
  user_id: number;
  username: string;
  is_admin: boolean;
  message: string;
  created_at: string;
}

export interface CreateTicketRequest {
  subject: string;
  category: string;
  priority?: string;
  description: string;
}

/**
 * Create a new support ticket
 */
export const createTicket = async (data: CreateTicketRequest): Promise<SupportTicket> => {
  const response = await apiClient.post('/support/tickets', data);
  return response.data;
};

/**
 * Get current user's tickets
 */
export const getMyTickets = async (): Promise<{ tickets: SupportTicket[] }> => {
  const response = await apiClient.get('/support/tickets');
  return response.data;
};

/**
 * Get ticket details with messages
 */
export const getTicketDetail = async (ticketId: number): Promise<{ ticket: SupportTicket; messages: TicketMessage[] }> => {
  const response = await apiClient.get(`/support/tickets/${ticketId}`);
  return response.data;
};

/**
 * Add a message to a ticket
 */
export const addTicketMessage = async (ticketId: number, message: string): Promise<TicketMessage> => {
  const response = await apiClient.post(`/support/tickets/${ticketId}/messages`, { message });
  return response.data;
};

// ============================================================
// Admin Support Ticket Endpoints
// ============================================================

export interface AdminTicketUpdate {
  status?: string;
  priority?: string;
  admin_notes?: string;
}

/**
 * Admin: Get all support tickets (optionally filtered by status)
 */
export const adminGetTickets = async (status?: string): Promise<{ tickets: SupportTicket[] }> => {
  const params = status ? `?status=${status}` : '';
  const response = await apiClient.get(`/admin/support/tickets${params}`);
  return response.data;
};

/**
 * Admin: Update a ticket (status, priority, admin_notes)
 */
export const adminUpdateTicket = async (ticketId: number, data: AdminTicketUpdate): Promise<SupportTicket> => {
  const response = await apiClient.put(`/admin/support/tickets/${ticketId}`, data);
  return response.data;
};

/**
 * Admin: Add a reply to a ticket (as admin)
 */
export const adminAddTicketMessage = async (ticketId: number, message: string): Promise<TicketMessage> => {
  const response = await apiClient.post(`/admin/support/tickets/${ticketId}/messages`, { message });
  return response.data;
};

// ============================================================
// GDPR Endpoints (EU General Data Protection Regulation)
// Full compliance with GDPR Articles 15, 17, 20
// ============================================================

/**
 * GDPR Consent settings
 */
export interface GDPRConsent {
  user_id: number;
  marketing_emails: boolean;
  analytics_tracking: boolean;
  third_party_sharing: boolean;
  personalization: boolean;
  voice_data_retention: boolean;
  consent_version: string;
  last_updated: string;
  ip_address?: string;
}

export interface GDPRConsentUpdate {
  marketing_emails?: boolean;
  analytics_tracking?: boolean;
  third_party_sharing?: boolean;
  personalization?: boolean;
  voice_data_retention?: boolean;
}

export interface GDPRConsentHistoryItem {
  id: number;
  user_id: number;
  field_changed: string;
  old_value: number | null;
  new_value: number;
  consent_version: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface GDPRDataExport {
  export_id: string;
  status: 'pending' | 'processing' | 'ready' | 'expired' | 'failed';
  format: string;
  requested_at: string;
  completed_at?: string;
  download_url?: string;
  expires_at?: string;
  file_size_bytes?: number;
}

export interface GDPRDataExportRequest {
  format?: 'json' | 'csv';
  include_voice_history?: boolean;
  include_usage_logs?: boolean;
}

export interface GDPRDataAccess {
  user_data: Record<string, unknown>;
  personal_data: Record<string, unknown>;
  billing_data: Record<string, unknown>;
  usage_data: Record<string, unknown>;
  consent_history: GDPRConsentHistoryItem[];
  data_categories: string[];
  retention_periods: Record<string, string>;
  third_party_recipients: string[];
}

export interface GDPRDeletionRequest {
  request_id: string;
  status: 'pending' | 'scheduled' | 'completed' | 'cancelled';
  reason?: string;
  requested_at: string;
  scheduled_deletion_date: string;
  can_cancel_until: string;
}

/**
 * Get user's GDPR consent settings
 */
export const getGDPRConsents = async (): Promise<GDPRConsent> => {
  const response = await apiClient.get('/gdpr/consents');
  return response.data;
};

/**
 * Update user's GDPR consent settings
 * All changes are logged to audit trail as required by GDPR
 */
export const updateGDPRConsents = async (updates: GDPRConsentUpdate): Promise<GDPRConsent> => {
  const response = await apiClient.put('/gdpr/consents', updates);
  return response.data;
};

/**
 * Get user's consent change history (audit trail)
 */
export const getGDPRConsentHistory = async (limit: number = 50): Promise<{ history: GDPRConsentHistoryItem[]; total: number }> => {
  const response = await apiClient.get('/gdpr/consent-history', { params: { limit } });
  return response.data;
};

/**
 * GDPR Article 15 - Right of Access
 * Get all personal data stored about the user
 */
export const getGDPRDataAccess = async (): Promise<GDPRDataAccess> => {
  const response = await apiClient.get('/gdpr/data-access');
  return response.data;
};

/**
 * GDPR Article 20 - Right to Data Portability
 * Request export of all personal data
 */
export const requestGDPRDataExport = async (request: GDPRDataExportRequest = {}): Promise<GDPRDataExport> => {
  const response = await apiClient.post('/gdpr/data-export', request);
  return response.data;
};

/**
 * List user's data export requests
 */
export const getGDPRDataExports = async (): Promise<{ exports: GDPRDataExport[] }> => {
  const response = await apiClient.get('/gdpr/data-exports');
  return response.data;
};

/**
 * Get pending deletion request
 */
export const getGDPRDeletionRequest = async (): Promise<{ has_pending_request: boolean; request: GDPRDeletionRequest | null }> => {
  const response = await apiClient.get('/gdpr/deletion-request');
  return response.data;
};

/**
 * GDPR Article 17 - Right to Erasure
 * Request account deletion with 30-day cooling-off period
 */
export const requestGDPRAccountDeletion = async (
  password: string,
  confirmDeletion: boolean,
  reason?: string
): Promise<GDPRDeletionRequest> => {
  const response = await apiClient.post('/gdpr/deletion-request', {
    password,
    confirm_deletion: confirmDeletion,
    reason
  });
  return response.data;
};

/**
 * Cancel a pending deletion request
 */
export const cancelGDPRDeletionRequest = async (requestId: string): Promise<{ message: string; request_id: string; status: string }> => {
  const response = await apiClient.delete(`/gdpr/deletion-request/${requestId}`);
  return response.data;
};

// ============================================================
// COGITO Knowledge Graph API
// ============================================================

/**
 * COGITO Entity interface
 */
export interface CogitoEntity {
  id: string;
  user_id: number;
  name: string;
  entity_type: string;
  description: string;
  attributes: string;  // JSON string
  confidence: number;
  created_at: string;
  updated_at: string;
}

/**
 * COGITO Relationship interface
 */
export interface CogitoRelationship {
  id: string;
  user_id: number;
  source_entity_id: string;
  target_entity_id: string;
  relationship_type: string;
  description: string | null;
  confidence: number;
  created_at: string;
  updated_at: string;
  source_name: string;
  source_type: string;
  target_name: string;
  target_type: string;
}

/**
 * COGITO Insight interface
 */
export interface CogitoInsight {
  id: string;
  user_id: number;
  content: string;
  category: string;
  importance: string;
  related_entities: string;  // JSON string
  created_at: string;
}

/**
 * COGITO Knowledge Graph response
 */
export interface CogitoGraphResponse {
  entities: CogitoEntity[];
  relationships: CogitoRelationship[];
  insights: CogitoInsight[];
  entity_count: number;
  relationship_count: number;
  insight_count: number;
}

/**
 * Get the complete COGITO knowledge graph for the current user
 */
export const getCogitoGraph = async (): Promise<CogitoGraphResponse> => {
  const response = await apiClient.get('/cogito/graph');
  return response.data;
};

/**
 * Get the COGITO knowledge graph as Mermaid diagram syntax
 */
export const getCogitoMermaid = async (): Promise<{ mermaid: string }> => {
  const response = await apiClient.get('/cogito/mermaid');
  return response.data;
};

/**
 * Get all COGITO entities for the current user
 */
export const getCogitoEntities = async (): Promise<{ entities: CogitoEntity[]; count: number }> => {
  const response = await apiClient.get('/cogito/entities');
  return response.data;
};

/**
 * Get all COGITO relationships for the current user
 */
export const getCogitoRelationships = async (): Promise<{ relationships: CogitoRelationship[]; count: number }> => {
  const response = await apiClient.get('/cogito/relationships');
  return response.data;
};

/**
 * Get all COGITO insights for the current user
 */
export const getCogitoInsights = async (): Promise<{ insights: CogitoInsight[]; count: number }> => {
  const response = await apiClient.get('/cogito/insights');
  return response.data;
};

/**
 * Reset/clear the COGITO knowledge graph for the current user
 */
export const resetCogitoGraph = async (): Promise<{ message: string }> => {
  const response = await apiClient.delete('/cogito/graph');
  return response.data;
};

/**
 * Delete a specific entity from the COGITO knowledge graph
 */
export const deleteCogitoEntity = async (entityId: string): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/cogito/entities/${entityId}`);
  return response.data;
};

/**
 * Delete a specific relationship from the COGITO knowledge graph
 */
export const deleteCogitoRelationship = async (relationshipId: string): Promise<{ message: string }> => {
  const response = await apiClient.delete(`/cogito/relationships/${relationshipId}`);
  return response.data;
};

/**
 * Force COGITO to process any accumulated transcripts immediately
 */
export const forceCogitoProcess = async (): Promise<{ message: string; operations_count?: number; processing_time_ms?: number }> => {
  const response = await apiClient.post('/cogito/process');
  return response.data;
};

// ============================================================
// Knowledge Base Files API
// ============================================================

import type { FileNode } from '@/types/files';

export const ensureKbFolder = async (kbId: string): Promise<{ folder_id: string; path: string }> => {
  const response = await apiClient.post(`/personal-data/knowledge/${kbId}/files/ensure-folder`);
  return response.data;
};

export const listKbFiles = async (kbId: string): Promise<FileNode[]> => {
  const response = await apiClient.get(`/personal-data/knowledge/${kbId}/files`);
  return response.data;
};

export const upgradeKbFile = async (kbId: string, fileId: string): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.post(`/personal-data/knowledge/${kbId}/files/${fileId}/upgrade`);
  return response.data;
};

export const copyDiskFileToKb = async (kbId: string, fileId: string): Promise<{ file_id: string; name: string; message: string }> => {
  const response = await apiClient.post(`/personal-data/knowledge/${kbId}/files/copy-from-disk`, { file_id: fileId });
  return response.data;
};

export const deleteKbFile = async (kbId: string, fileId: string): Promise<void> => {
  await apiClient.delete(`/personal-data/knowledge/${kbId}/files/${fileId}`);
};

// ============================================================
// Task Management API
// ============================================================

import type {
  Task,
  TaskDependency,
  TaskHistory,
  TaskStats,
  TaskGraphResponse,
  CreateTaskRequest,
  UpdateTaskRequest,
  ReorderTaskRequest,
  AddDependencyRequest,
  UpdatePositionsRequest,
  BulkCreateRequest,
  BulkStatusUpdateRequest,
  TasksFilter,
  AgentAssignee,
  AssignableUser,
} from '@/types/tasks';

/**
 * Build query string from filters
 */
const buildTasksQueryParams = (filters?: TasksFilter): string => {
  if (!filters) return '';

  const params = new URLSearchParams();

  if (filters.status) {
    const statuses = Array.isArray(filters.status) ? filters.status.join(',') : filters.status;
    params.append('status', statuses);
  }
  if (filters.assigned_to) params.append('assigned_to', filters.assigned_to);
  if (filters.created_by) params.append('created_by', filters.created_by);
  if (filters.priority) {
    const priorities = Array.isArray(filters.priority) ? filters.priority.join(',') : filters.priority;
    params.append('priority', priorities);
  }
  if (filters.search) params.append('search', filters.search);
  if (filters.due_before) params.append('due_before', filters.due_before);
  if (filters.due_after) params.append('due_after', filters.due_after);
  if (filters.include_dependencies) params.append('include_dependencies', 'true');
  if (filters.project_id) params.append('project_id', filters.project_id);

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

/**
 * List all tasks with optional filters
 */
export const getTasks = async (filters?: TasksFilter): Promise<Task[]> => {
  const queryString = buildTasksQueryParams(filters);
  const response = await apiClient.get(`/tasks${queryString}`);
  return response.data;
};

/**
 * Get available agents for task assignment.
 * Returns the user's self-agent first (displayed as "Me"),
 * followed by enabled AI agents the user has access to.
 */
export const getAvailableAssignees = async (): Promise<AgentAssignee[]> => {
  const response = await apiClient.get('/agents/assignees');
  return response.data;
};

/**
 * Get task statistics
 */
export const getTaskStats = async (): Promise<TaskStats> => {
  const response = await apiClient.get('/tasks/stats');
  return response.data;
};

/**
 * Get task graph for React Flow visualization
 */
export const getTaskGraph = async (): Promise<TaskGraphResponse> => {
  const response = await apiClient.get('/tasks/graph');
  return response.data;
};

/**
 * Get a single task by ID
 */
export const getTask = async (taskId: string): Promise<Task> => {
  const response = await apiClient.get(`/tasks/${taskId.toUpperCase()}`);
  return response.data;
};

/**
 * Create a new task
 */
export const createTask = async (data: CreateTaskRequest): Promise<Task> => {
  const response = await apiClient.post('/tasks', data);
  return response.data;
};

/**
 * Update an existing task
 */
export const updateTask = async (taskId: string, data: UpdateTaskRequest): Promise<Task> => {
  const response = await apiClient.put(`/tasks/${taskId.toUpperCase()}`, data);
  return response.data;
};

/**
 * Delete a task
 */
export const deleteTask = async (taskId: string): Promise<void> => {
  await apiClient.delete(`/tasks/${taskId.toUpperCase()}`);
};

/**
 * Reorder a task (for Kanban drag-and-drop)
 */
export const reorderTask = async (taskId: string, data: ReorderTaskRequest): Promise<Task> => {
  const response = await apiClient.post(`/tasks/${taskId.toUpperCase()}/reorder`, data);
  return response.data;
};

/**
 * Bulk update task positions (for graph view drag)
 */
export const updateTaskPositions = async (data: UpdatePositionsRequest): Promise<{ success: boolean; updated: number }> => {
  const response = await apiClient.put('/tasks/positions', data);
  return response.data;
};

/**
 * Get task dependencies
 */
export const getTaskDependencies = async (taskId: string): Promise<TaskDependency[]> => {
  const response = await apiClient.get(`/tasks/${taskId.toUpperCase()}/dependencies`);
  return response.data;
};

/**
 * Add a dependency to a task
 */
export const addTaskDependency = async (taskId: string, data: AddDependencyRequest): Promise<TaskDependency> => {
  const response = await apiClient.post(`/tasks/${taskId.toUpperCase()}/dependencies`, data);
  return response.data;
};

/**
 * Remove a task dependency
 */
export const removeTaskDependency = async (taskId: string, dependencyId: string): Promise<void> => {
  await apiClient.delete(`/tasks/${taskId.toUpperCase()}/dependencies/${dependencyId.toUpperCase()}`);
};

/**
 * Get task history
 */
export const getTaskHistory = async (taskId: string): Promise<TaskHistory[]> => {
  const response = await apiClient.get(`/tasks/${taskId.toUpperCase()}/history`);
  return response.data;
};

/**
 * Get assignable users for task assignment (institutional tenants)
 */
export const getAssignableUsers = async (search?: string): Promise<AssignableUser[]> => {
  const params = search ? { search } : {};
  const response = await apiClient.get('/tasks/assignable-users', { params });
  return response.data;
};

/**
 * Get or create a chat channel for a task
 */
export const getTaskChat = async (taskId: string, chatType: 'author_notes' | 'discussion'): Promise<{ channel_id: string }> => {
  const response = await apiClient.get(`/tasks/${taskId.toUpperCase()}/chat/${chatType}`);
  return response.data;
};

/**
 * Bulk create tasks
 */
export const bulkCreateTasks = async (data: BulkCreateRequest): Promise<Task[]> => {
  const response = await apiClient.post('/tasks/bulk', data);
  return response.data;
};

/**
 * Bulk update task statuses
 */
export const bulkUpdateTaskStatus = async (data: BulkStatusUpdateRequest): Promise<{ success: boolean; updated: number; requested: number }> => {
  const response = await apiClient.put('/tasks/bulk/status', data);
  return response.data;
};

// ============================================================
// Task Archive API
// ============================================================

import type {
  ArchiveStats,
  BulkArchiveResult,
  AutoArchiveResult,
} from '@/types/tasks';

/**
 * Get archive statistics
 */
export const getArchiveStats = async (): Promise<ArchiveStats> => {
  const response = await apiClient.get('/tasks/archive/stats');
  return response.data;
};

/**
 * Get archived tasks
 */
export const getArchivedTasks = async (filters?: { status?: string; search?: string }): Promise<Task[]> => {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.search) params.append('search', filters.search);

  const queryString = params.toString();
  const response = await apiClient.get(`/tasks/archived${queryString ? `?${queryString}` : ''}`);
  return response.data;
};

/**
 * Start working on a self-assigned task.
 * Creates a draft iteration and sets status to 'in_progress'.
 * Only works for tasks assigned to user's self-agent (not AI agents).
 */
export const startUserTask = async (taskId: string): Promise<Task> => {
  const response = await apiClient.post(`/tasks/${taskId}/start`);
  return response.data;
};

/**
 * Archive a single task
 */
export const archiveTask = async (taskId: string): Promise<Task> => {
  const response = await apiClient.post(`/tasks/${taskId}/archive`);
  return response.data;
};

/**
 * Unarchive a task (restore from archive)
 */
export const unarchiveTask = async (taskId: string): Promise<Task> => {
  const response = await apiClient.post(`/tasks/${taskId}/unarchive`);
  return response.data;
};

/**
 * Bulk archive multiple tasks
 */
export const bulkArchiveTasks = async (taskIds: string[]): Promise<BulkArchiveResult> => {
  const response = await apiClient.post('/tasks/archive/bulk', { task_ids: taskIds });
  return response.data;
};

/**
 * Auto-archive old completed/cancelled tasks
 */
export const autoArchiveTasks = async (options?: { completed_days?: number; cancelled_days?: number }): Promise<AutoArchiveResult> => {
  const response = await apiClient.post('/tasks/archive/auto', options || {});
  return response.data;
};

// ============================================================
// Task Attachments API
// ============================================================

import type {
  TaskAttachment,
  AttachmentUploadResponse,
  AttachmentDownloadResponse,
} from '@/types/tasks';

/**
 * Get all attachments for a task
 */
export const getTaskAttachments = async (taskId: string): Promise<TaskAttachment[]> => {
  const response = await apiClient.get(`/tasks/${taskId.toUpperCase()}/attachments`);
  return response.data;
};

/**
 * Upload a file attachment to a task via multipart form upload.
 *
 * @deprecated Use uploadTaskAttachmentWithProgress() for reliable large file uploads.
 * This function fails with 413 for files >5-10MB due to Lambda payload limits.
 * The presigned URL approach in uploadTaskAttachmentWithProgress() supports files up to 100MB.
 */
export const uploadTaskAttachment = async (taskId: string, file: File): Promise<AttachmentUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post(
    `/tasks/${taskId.toUpperCase()}/attachments`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

/**
 * Get a presigned URL to download an attachment
 */
export const getAttachmentDownloadUrl = async (
  taskId: string,
  attachmentId: string
): Promise<AttachmentDownloadResponse> => {
  const response = await apiClient.get(
    `/tasks/${taskId.toUpperCase()}/attachments/${attachmentId.toUpperCase()}/download`
  );
  return response.data;
};

/**
 * Delete an attachment from a task
 */
export const deleteTaskAttachment = async (taskId: string, attachmentId: string): Promise<void> => {
  await apiClient.delete(`/tasks/${taskId.toUpperCase()}/attachments/${attachmentId.toUpperCase()}`);
};

// ============================================================
// PRESIGNED UPLOAD FOR LARGE ATTACHMENTS (>5MB, up to 100MB)
// ============================================================

interface PresignedAttachmentUploadResponse {
  upload_url: string;
  attachment_id: string;
  s3_key: string;
  expires_in: number;
}

interface AttachmentUploadCompleteResponse {
  success: boolean;
  attachment: TaskAttachment | null;
  message: string;
}

/**
 * Progress info for presigned uploads
 */
export interface AttachmentUploadProgress {
  attachmentId?: string;
  filename: string;
  fileSize: number;
  bytesUploaded: number;
  percentage: number;
  speed: number; // bytes per second
  estimatedTimeRemaining: number; // seconds
  status: 'pending' | 'uploading' | 'completing' | 'completed' | 'error' | 'cancelled';
  error?: string;
}

/**
 * Get a presigned URL for direct S3 upload (large files)
 */
const getAttachmentPresignedUploadUrl = async (
  taskId: string,
  filename: string,
  contentType: string,
  fileSize: number,
  iterationId?: string | null
): Promise<PresignedAttachmentUploadResponse> => {
  const response = await apiClient.post(`/tasks/${taskId.toUpperCase()}/attachments/upload-url`, {
    filename,
    content_type: contentType,
    file_size: fileSize,
    iteration_id: iterationId
  });
  return response.data;
};

/**
 * Confirm presigned upload completion
 */
const completeAttachmentUpload = async (taskId: string, attachmentId: string): Promise<AttachmentUploadCompleteResponse> => {
  const response = await apiClient.post(
    `/tasks/${taskId.toUpperCase()}/attachments/upload-complete/${attachmentId.toUpperCase()}`
  );
  return response.data;
};

/**
 * Cancel a pending presigned upload
 */
export const cancelAttachmentUpload = async (taskId: string, attachmentId: string): Promise<void> => {
  await apiClient.delete(`/tasks/${taskId.toUpperCase()}/attachments/upload-cancel/${attachmentId.toUpperCase()}`);
};

/**
 * Upload a task attachment with real-time progress tracking via presigned URL.
 * This bypasses Lambda/API Gateway limits by uploading directly to S3.
 * Supports files up to 100MB.
 */
export const uploadTaskAttachmentWithProgress = async (
  taskId: string,
  file: File,
  onProgress: (progress: AttachmentUploadProgress) => void,
  abortSignal?: AbortSignal,
  iterationId?: string | null
): Promise<TaskAttachment> => {
  const startTime = Date.now();
  let lastProgressTime = startTime;
  let lastBytesUploaded = 0;

  const progress: AttachmentUploadProgress = {
    filename: file.name,
    fileSize: file.size,
    bytesUploaded: 0,
    percentage: 0,
    speed: 0,
    estimatedTimeRemaining: 0,
    status: 'pending'
  };

  try {
    // Step 1: Get presigned URL
    const presigned = await getAttachmentPresignedUploadUrl(
      taskId,
      file.name,
      file.type || 'application/octet-stream',
      file.size,
      iterationId
    );
    progress.attachmentId = presigned.attachment_id;

    // Step 2: Upload to S3 with progress tracking
    progress.status = 'uploading';
    onProgress({ ...progress });

    // Import axios dynamically for the direct S3 upload
    const axios = (await import('axios')).default;

    await axios.put(presigned.upload_url, file, {
      headers: {
        'Content-Type': file.type || 'application/octet-stream'
      },
      signal: abortSignal,
      onUploadProgress: (event) => {
        const now = Date.now();
        const bytesUploaded = event.loaded || 0;
        const timeDelta = (now - lastProgressTime) / 1000; // seconds

        // Calculate speed (bytes per second)
        if (timeDelta > 0.1) {
          const bytesDelta = bytesUploaded - lastBytesUploaded;
          progress.speed = bytesDelta / timeDelta;
          lastProgressTime = now;
          lastBytesUploaded = bytesUploaded;
        }

        // Calculate percentage and ETA
        progress.bytesUploaded = bytesUploaded;
        progress.percentage = event.total ? Math.round((bytesUploaded / event.total) * 100) : 0;

        if (progress.speed > 0) {
          const remainingBytes = file.size - bytesUploaded;
          progress.estimatedTimeRemaining = Math.ceil(remainingBytes / progress.speed);
        }

        onProgress({ ...progress });
      }
    });

    // Step 3: Confirm upload completion
    progress.status = 'completing';
    progress.percentage = 100;
    onProgress({ ...progress });

    const result = await completeAttachmentUpload(taskId, presigned.attachment_id);

    if (result.success && result.attachment) {
      progress.status = 'completed';
      onProgress({ ...progress });
      return result.attachment;
    } else {
      throw new Error(result.message || 'Upload confirmation failed');
    }
  } catch (error) {
    // Handle abort
    if (error instanceof Error && error.name === 'CanceledError') {
      progress.status = 'cancelled';
      progress.error = 'Upload cancelled';
      onProgress({ ...progress });

      // Try to clean up the placeholder
      if (progress.attachmentId) {
        try {
          await cancelAttachmentUpload(taskId, progress.attachmentId);
        } catch {
          // Ignore cleanup errors
        }
      }
      throw error;
    }

    progress.status = 'error';
    progress.error = error instanceof Error ? error.message : 'Upload failed';
    onProgress({ ...progress });
    throw error;
  }
};

// ============================================================
// ITERATIONS API
// ============================================================

import type {
  TaskIteration,
  IterationListResponse,
  IterationAttachment,
  IterationAttachmentUploadResponse,
  CreateIterationRequest,
  UpdateIterationRequest,
  CompleteIterationRequest,
  ArtifactType,
} from '@/types/tasks';

/**
 * Get all iterations for a task
 */
export const getTaskIterations = async (
  taskId: string,
  includeAttachments: boolean = false
): Promise<IterationListResponse> => {
  const params = includeAttachments ? '?include_attachments=true' : '';
  const response = await apiClient.get(`/tasks/${taskId.toUpperCase()}/iterations${params}`);
  return response.data;
};

/**
 * Get a single iteration with attachments
 */
export const getIteration = async (taskId: string, iterationId: string): Promise<TaskIteration> => {
  const response = await apiClient.get(
    `/tasks/${taskId.toUpperCase()}/iterations/${iterationId.toUpperCase()}`
  );
  return response.data;
};

/**
 * Create a new iteration (starts in 'draft' status)
 */
export const createIteration = async (
  taskId: string,
  data: CreateIterationRequest
): Promise<TaskIteration> => {
  const response = await apiClient.post(`/tasks/${taskId.toUpperCase()}/iterations`, data);
  return response.data;
};

/**
 * Update an iteration (only in 'draft' status)
 */
export const updateIteration = async (
  taskId: string,
  iterationId: string,
  data: UpdateIterationRequest
): Promise<TaskIteration> => {
  const response = await apiClient.patch(
    `/tasks/${taskId.toUpperCase()}/iterations/${iterationId.toUpperCase()}`,
    data
  );
  return response.data;
};

/**
 * Submit an iteration for processing (changes status to 'pending')
 */
export const submitIteration = async (taskId: string, iterationId: string): Promise<TaskIteration> => {
  const response = await apiClient.post(
    `/tasks/${taskId.toUpperCase()}/iterations/${iterationId.toUpperCase()}/submit`
  );
  return response.data;
};

/**
 * Manually complete an iteration with a report
 */
export const completeIteration = async (
  taskId: string,
  iterationId: string,
  data: CompleteIterationRequest
): Promise<TaskIteration> => {
  const response = await apiClient.post(
    `/tasks/${taskId.toUpperCase()}/iterations/${iterationId.toUpperCase()}/complete`,
    data
  );
  return response.data;
};

/**
 * Cancel an iteration (only in 'draft' or 'pending' status)
 */
export const cancelIteration = async (taskId: string, iterationId: string): Promise<TaskIteration> => {
  const response = await apiClient.post(
    `/tasks/${taskId.toUpperCase()}/iterations/${iterationId.toUpperCase()}/cancel`
  );
  return response.data;
};

/**
 * Delete an iteration (only in 'draft' status)
 */
export const deleteIteration = async (taskId: string, iterationId: string): Promise<void> => {
  await apiClient.delete(
    `/tasks/${taskId.toUpperCase()}/iterations/${iterationId.toUpperCase()}`
  );
};

/**
 * Mark all iterations as read for a task
 */
export const markIterationsRead = async (taskId: string): Promise<void> => {
  await apiClient.post(`/tasks/${taskId.toUpperCase()}/iterations/mark-read`);
};

/**
 * Rate a completed/failed iteration with 1-5 stars and optional feedback
 */
export const rateIteration = async (
  taskId: string,
  iterationId: string,
  stars: number,
  feedback?: string
): Promise<TaskIteration> => {
  const response = await apiClient.post(
    `/tasks/${taskId.toUpperCase()}/iterations/${iterationId.toUpperCase()}/rate`,
    { stars, feedback }
  );
  return response.data;
};

/**
 * Generate a revision prompt from user feedback using Haiku
 */
export const suggestRevisionPrompt = async (
  taskId: string,
  iterationId: string,
  feedback: string
): Promise<{ suggested_prompt: string }> => {
  const response = await apiClient.post(
    `/tasks/${taskId.toUpperCase()}/iterations/${iterationId.toUpperCase()}/suggest-revision`,
    { feedback }
  );
  return response.data;
};

/**
 * Get attachments for an iteration
 */
export const getIterationAttachments = async (
  taskId: string,
  iterationId: string,
  artifactType?: ArtifactType
): Promise<IterationAttachment[]> => {
  const params = artifactType ? `?artifact_type=${artifactType}` : '';
  const response = await apiClient.get(
    `/tasks/${taskId.toUpperCase()}/iterations/${iterationId.toUpperCase()}/attachments${params}`
  );
  return response.data;
};

/**
 * Upload a file to an iteration
 */
export const uploadIterationAttachment = async (
  taskId: string,
  iterationId: string,
  file: File,
  artifactType: ArtifactType = 'input'
): Promise<IterationAttachmentUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('artifact_type', artifactType);

  const response = await apiClient.post(
    `/tasks/${taskId.toUpperCase()}/iterations/${iterationId.toUpperCase()}/attachments`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data;
};

/**
 * Get download URL for an iteration attachment
 */
export const getIterationAttachmentDownloadUrl = async (
  taskId: string,
  iterationId: string,
  attachmentId: string
): Promise<AttachmentDownloadResponse> => {
  const response = await apiClient.get(
    `/tasks/${taskId.toUpperCase()}/iterations/${iterationId.toUpperCase()}/attachments/${attachmentId.toUpperCase()}/download`
  );
  return response.data;
};

/**
 * Delete an iteration attachment
 */
export const deleteIterationAttachment = async (
  taskId: string,
  iterationId: string,
  attachmentId: string
): Promise<void> => {
  await apiClient.delete(
    `/tasks/${taskId.toUpperCase()}/iterations/${iterationId.toUpperCase()}/attachments/${attachmentId.toUpperCase()}`
  );
};

// Re-export task types for convenience
export type {
  Task,
  TaskDependency,
  TaskHistory,
  TaskStats,
  TaskGraphResponse,
  CreateTaskRequest,
  UpdateTaskRequest,
  ReorderTaskRequest,
  AddDependencyRequest,
  UpdatePositionsRequest,
  BulkCreateRequest,
  BulkStatusUpdateRequest,
  TasksFilter,
  TaskStatus,
  TaskActor,
  TaskPriority,
  ArchiveStats,
  BulkArchiveResult,
  AutoArchiveResult,
  TaskAttachment,
  AttachmentUploadResponse,
  AttachmentDownloadResponse,
  // Iteration types
  TaskIteration,
  IterationListResponse,
  IterationAttachment,
  IterationAttachmentUploadResponse,
  CreateIterationRequest,
  UpdateIterationRequest,
  CompleteIterationRequest,
  IterationStatus,
  ArtifactType,
  IterationCreatedBy,
} from '@/types/tasks';

// ============================================================
// Sync API (CalDAV Calendar / IMAP Email Sync)
// ============================================================

export type SyncAccountType = 'caldav' | 'imap' | 'smtp';
export type SyncStatus = 'success' | 'failed' | 'pending' | 'syncing';

export interface SyncAccount {
  id: string;
  user_id: number;
  account_type: SyncAccountType;
  display_name: string;
  server_host: string;
  server_port: number;
  username: string;
  use_ssl: boolean;
  is_enabled: boolean;
  last_sync_at: string | null;
  last_sync_status: SyncStatus | null;
  last_sync_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateSyncAccountRequest {
  account_type: SyncAccountType;
  display_name: string;
  server_host: string;
  server_port: number;
  username: string;
  password: string;
  use_ssl?: boolean;
  is_enabled?: boolean;
}

export interface UpdateSyncAccountRequest {
  display_name?: string;
  server_host?: string;
  server_port?: number;
  username?: string;
  password?: string;
  use_ssl?: boolean;
  is_enabled?: boolean;
}

export interface SyncCalendarInfo {
  url: string;
  name: string;
  color: string | null;
  description: string | null;
  is_enabled: boolean;
}

export interface SyncFolderInfo {
  name: string;
  message_count: number;
  unseen_count: number;
  is_enabled: boolean;
}

export interface SyncTestResult {
  success: boolean;
  message: string;
  server_info?: Record<string, unknown>;
}

export interface SyncResult {
  success: boolean;
  message: string;
  events_synced?: number;
  events_pushed?: number;
  emails_synced?: number;
  errors?: string[];
}

/**
 * Get all sync accounts for the current user
 */
export const getSyncAccounts = async (accountType?: SyncAccountType): Promise<SyncAccount[]> => {
  const params = accountType ? `?account_type=${accountType}` : '';
  const response = await apiClient.get(`/sync/accounts${params}`);
  return response.data;
};

/**
 * Get a single sync account by ID
 */
export const getSyncAccount = async (accountId: string): Promise<SyncAccount> => {
  const response = await apiClient.get(`/sync/accounts/${accountId}`);
  return response.data;
};

/**
 * Create a new sync account
 */
export const createSyncAccount = async (data: CreateSyncAccountRequest): Promise<SyncAccount> => {
  const response = await apiClient.post('/sync/accounts', data);
  return response.data;
};

/**
 * Update an existing sync account
 */
export const updateSyncAccount = async (accountId: string, data: UpdateSyncAccountRequest): Promise<SyncAccount> => {
  const response = await apiClient.put(`/sync/accounts/${accountId}`, data);
  return response.data;
};

/**
 * Delete a sync account
 */
export const deleteSyncAccount = async (accountId: string): Promise<void> => {
  await apiClient.delete(`/sync/accounts/${accountId}`);
};

/**
 * Test sync account connection
 */
export const testSyncAccount = async (accountId: string): Promise<SyncTestResult> => {
  const response = await apiClient.post(`/sync/accounts/${accountId}/test`);
  return response.data;
};

/**
 * Trigger manual sync for an account
 */
export const triggerSync = async (accountId: string): Promise<SyncResult> => {
  const response = await apiClient.post(`/sync/accounts/${accountId}/sync`);
  return response.data;
};

/**
 * Get CalDAV calendars for an account
 */
export const getSyncCalendars = async (accountId: string): Promise<SyncCalendarInfo[]> => {
  const response = await apiClient.get(`/sync/accounts/${accountId}/calendars`);
  return response.data.calendars;
};

/**
 * Get IMAP folders for an account
 */
export const getSyncFolders = async (accountId: string): Promise<SyncFolderInfo[]> => {
  const response = await apiClient.get(`/sync/accounts/${accountId}/folders`);
  return response.data.folders;
};

// ============================================================
// Avatar Upload API
// ============================================================

export interface AvatarUploadUrlResponse {
  upload_url: string;
  s3_key: string;
  expires_in: number;
}

/**
 * Request a presigned URL for avatar upload
 */
export const requestAvatarUploadUrl = async (
  content_type: string,
  file_size: number
): Promise<AvatarUploadUrlResponse> => {
  const response = await apiClient.post('/settings/avatar/upload-url', {
    content_type,
    file_size,
  });
  return response.data;
};

/**
 * Confirm avatar upload after S3 PUT completes
 */
export const confirmAvatarUpload = async (
  s3_key: string
): Promise<{ avatar_url: string }> => {
  const response = await apiClient.post('/settings/avatar/confirm', { s3_key });
  return response.data;
};

/**
 * Delete current user's avatar
 */
export const deleteAvatar = async (): Promise<{ success: boolean }> => {
  const response = await apiClient.delete('/settings/avatar');
  return response.data;
};

// ============================================================
// Cloud Disk ↔ Tasks Integration
// ============================================================

export interface TaskFolderInfo {
  folder_id: string | null;
  path: string | null;
}

export interface TaskFileSearchResult {
  file_id: string;
  name: string;
  path?: string;
  content_type?: string;
  rank?: number;
  similarity?: number;
  hybrid_score?: number;
  headline?: string;
  chunk_text?: string;
}

export interface TaskFileSearchResponse {
  results: TaskFileSearchResult[];
  total: number;
  query: string;
  mode: string;
  task_id: string;
}

export interface CopyFromDiskResponse {
  attachment_id: string;
  cloud_file_id: string;
  message: string;
}

/**
 * Get Cloud Disk folder for a task
 */
export const getTaskFolder = async (taskId: string): Promise<TaskFolderInfo> => {
  const response = await apiClient.get(`/tasks/${taskId.toUpperCase()}/folder`);
  return response.data;
};

/**
 * Search files scoped to a specific task
 */
export const searchTaskFiles = async (
  taskId: string,
  query: string,
  mode: 'fulltext' | 'semantic' | 'hybrid' = 'hybrid',
  limit: number = 20
): Promise<TaskFileSearchResponse> => {
  const response = await apiClient.post(`/tasks/${taskId.toUpperCase()}/files/search`, {
    query,
    mode,
    limit,
  });
  return response.data;
};

/**
 * Get processing status for all files attached to a task
 */
export interface TaskFilesProcessingResponseApi {
  task_id: string;
  files: Array<{
    file_id: string;
    file_name: string;
    content_type: string | null;
    file_size: number | null;
    page_count: number | null;
    status: string;
    processing_level: string | null;
    has_ocr: boolean;
    has_ascii: boolean;
    has_page_previews: boolean;
    has_scout: boolean;
    has_embeddings: boolean;
    has_ocr_embeddings: boolean;
    has_ascii_embeddings: boolean;
    has_sense_summary: boolean;
    ocr_pages_done: number;
    ascii_pages_done: number;
    preview_pages_done: number;
  }>;
  total_files: number;
  ready_files: number;
  all_ready: boolean;
}

export const getTaskFilesProcessingStatus = async (
  taskId: string
): Promise<TaskFilesProcessingResponseApi> => {
  const response = await apiClient.get<TaskFilesProcessingResponseApi>(
    `/tasks/${taskId.toUpperCase()}/files-processing-status`
  );
  return response.data;
};

/**
 * Copy an existing Cloud Disk file to a task (with all processing results)
 */
export const copyDiskFileToTask = async (
  taskId: string,
  fileId: string,
  iterationId?: string
): Promise<CopyFromDiskResponse> => {
  const response = await apiClient.post(
    `/tasks/${taskId.toUpperCase()}/attachments/copy-from-disk`,
    {
      file_id: fileId,
      iteration_id: iterationId || null,
    }
  );
  return response.data;
};

// ============================================================
// AI Chat Persistence
// ============================================================

export interface AiChatMessage {
  id: number;
  user_id: number;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
}

export const getAiChatMessages = async (limit?: number): Promise<AiChatMessage[]> => {
  const response = await apiClient.get('/ai-chat/messages', { params: { limit } });
  return response.data;
};

export const saveAiChatMessage = async (role: string, content: string): Promise<AiChatMessage> => {
  const response = await apiClient.post('/ai-chat/messages', { role, content });
  return response.data;
};

export const clearAiChatHistory = async (): Promise<{ deleted_count: number }> => {
  const response = await apiClient.delete('/ai-chat/messages');
  return response.data;
};

// ============================================================
// Export API Client (for advanced use cases)
// ============================================================

export default apiClient;
